import React from 'react';
import type { SplitHeroComponent as SplitHeroComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';

interface SplitHeroComponentProps {
  component: SplitHeroComponentType;
}

export const SplitHeroComponent: React.FC<SplitHeroComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Generate a unique ID for scoped responsive styles
  const componentId = `split-hero-${component.id}`;

  console.log('🎨 SplitHeroComponent: Using theme:', currentTheme);
  console.log('🎨 SplitHeroComponent: themeStyles.splitHero:', themeStyles.splitHero);

  // Title size classes
  const titleSizeClasses = {
    small: 'text-3xl md:text-4xl',
    medium: 'text-4xl md:text-5xl',
    large: 'text-5xl md:text-6xl lg:text-7xl',
  };

  // Alignment classes
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  // Helper to convert position enum to CSS background-position
  const getBackgroundPosition = (position?: string): string => {
    if (!position) return 'center center';
    const positionMap: Record<string, string> = {
      center: 'center center',
      top: 'center top',
      bottom: 'center bottom',
      left: 'left center',
      right: 'right center',
      'top-left': 'left top',
      'top-right': 'right top',
      'bottom-left': 'left bottom',
      'bottom-right': 'right bottom',
    };
    return positionMap[position] || 'center center';
  };

  const isCutout = data.imagePresentation === 'cutout';

  const isGradient = (bg?: string | null) =>
    !!bg && (bg.startsWith('linear-gradient') || bg.startsWith('radial-gradient'));

  // Resolved background for fill (text panel) or cutout (whole section)
  const resolvedBackground =
    data.customColors?.background ||
    themeStyles.splitHero?.background ||
    data.backgroundColor ||
    '#ffffff';

  const getSectionStyle = (): React.CSSProperties => {
    if (!isCutout) {
      return { backgroundColor: 'transparent' };
    }
    if (isGradient(resolvedBackground)) {
      return { backgroundImage: resolvedBackground };
    }
    return { backgroundColor: resolvedBackground };
  };

  // Fill mode: solid panel behind text. Cutout: transparent so section bg shows through.
  const textPanelBackground = isCutout ? 'transparent' : resolvedBackground;

  const titleColor = data.customColors?.title || themeStyles.splitHero?.titleColor || '#111827';

  // subtitleColor uses textColor as fallback so "Text Color" setting works
  const subtitleColor =
    data.customColors?.subtitle ||
    themeStyles.splitHero?.subtitleColor ||
    data.textColor ||
    '#6b7280';

  const taglineColor =
    data.customColors?.tagline || themeStyles.splitHero?.taglineColor || '#3b82f6';

  // Get button styles using theme colors
  const getButtonStyles = (style: string) => {
    const buttonBg = data.customColors?.buttonBg || themeStyles.splitHero?.buttonBg || '#3b82f6';
    const buttonText =
      data.customColors?.buttonText || themeStyles.splitHero?.buttonText || '#ffffff';

    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
    }
  };

  // Determine layout direction
  const isImageLeft = data.layout === 'image-left';

  // Responsive layout: stack on mobile, side-by-side on tablet+
  // No gaps between sections for flush edge-to-edge connection
  // Using md: breakpoint (768px) so changes are visible in editor
  const containerClasses = data.stackOnMobile
    ? 'flex flex-col md:flex-row items-stretch'
    : 'flex flex-row items-stretch';

  // Determine order based on:
  // 1. Mobile: mobileImageFirst determines if image shows first when stacked
  // 2. Desktop (md+): isImageLeft determines if image is on left or right
  const getMobileOrder = (isText: boolean) => {
    if (!data.stackOnMobile) return ''; // No mobile-specific order needed
    if (data.mobileImageFirst) {
      return isText ? 'order-2' : 'order-1';
    }
    return isText ? 'order-1' : 'order-2';
  };

  const getDesktopOrder = (isText: boolean) => {
    // For desktop: text is order-1 (left) by default, order-2 when image is left
    if (isImageLeft) {
      return isText ? 'md:order-2' : 'md:order-1';
    }
    return isText ? 'md:order-1' : 'md:order-2';
  };

  const textOrder = `${getMobileOrder(true)} ${getDesktopOrder(true)}`;
  const imageOrder = `${getMobileOrder(false)} ${getDesktopOrder(false)}`;

  // Responsive styles for width - applied via scoped style tag
  const responsiveStyles = data.stackOnMobile
    ? `
      @media (min-width: 768px) {
        #${componentId} .split-hero-text {
          width: ${data.textWidth}% !important;
          flex: 0 0 ${data.textWidth}% !important;
        }
        #${componentId} .split-hero-image {
          width: ${data.imageWidth}% !important;
          flex: 0 0 ${data.imageWidth}% !important;
        }
      }
    `
    : `
      #${componentId} .split-hero-text {
        width: ${data.textWidth}%;
        flex: 0 0 ${data.textWidth}%;
      }
      #${componentId} .split-hero-image {
        width: ${data.imageWidth}%;
        flex: 0 0 ${data.imageWidth}%;
      }
    `;

  const textMinHeight = isCutout
    ? 'min-h-[280px] md:min-h-[500px]'
    : 'min-h-[500px] md:min-h-[600px]';
  const imageMinHeight = isCutout
    ? 'min-h-[280px] md:min-h-[500px]'
    : 'min-h-[500px] md:min-h-[600px]';

  return (
    <section id={componentId} className="relative overflow-hidden" style={getSectionStyle()}>
      <style>{responsiveStyles}</style>
      <div className={containerClasses}>
        {/* Text Content Panel */}
        <div
          className={`split-hero-text ${textOrder} flex items-center justify-center ${textMinHeight}`}
          style={{
            backgroundColor: textPanelBackground,
            padding: '3rem 2.5rem',
            boxShadow: isCutout
              ? 'none'
              : isImageLeft
                ? '4px 0 6px rgba(0,0,0,0.1)'
                : '-4px 0 6px rgba(0,0,0,0.1)',
          }}
        >
          <div className={`w-full max-w-2xl ${alignmentClasses[data.alignment]}`}>
            {/* Tagline */}
            {data.tagline && (
              <p
                className="opacity-80 text-sm md:text-base font-medium uppercase tracking-wide mb-4"
                style={{ color: taglineColor }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.tagline),
                }}
              />
            )}

            {/* Title */}
            <h1
              className={`${titleSizeClasses[data.size]} font-bold mb-6 leading-tight`}
              style={{
                color: titleColor,
                whiteSpace: 'normal',
                ...headingFontStyle,
              }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.title),
              }}
            />

            {/* Subtitle */}
            {data.subtitle && (
              <div
                className={`text-lg md:text-xl opacity-90 mb-8 leading-relaxed rich-text-content`}
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: /<[a-z][\s\S]*>/i.test(data.subtitle)
                    ? data.subtitle
                    : renderTextWithLineBreaks(data.subtitle),
                }}
              />
            )}

            {/* Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-4 ${
                data.alignment === 'center'
                  ? 'justify-center'
                  : data.alignment === 'right'
                    ? 'justify-end'
                    : 'justify-start'
              }`}
            >
              {(data.buttons || []).map((button, index) => (
                <ButtonLink
                  key={index}
                  url={button.url}
                  target={(button as any).target}
                  className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block text-center"
                  style={getButtonStyles(button.style)}
                >
                  {button.text}
                </ButtonLink>
              ))}
            </div>
          </div>
        </div>

        {/* Image - Edge-to-edge, no rounded corners */}
        {data.image?.enabled &&
          (data.image.url || data.image.cloudfrontUrl) &&
          (() => {
            const hasEffects = hasCropApplied((data.image as any)?.crop);
            const cropStyles = getCroppedImageStyles((data.image as any)?.crop);
            const imageUrl = data.image.cloudfrontUrl || data.image.url || '';
            const rawSize = isCutout ? 'contain' : data.image.size || 'cover';
            const objectPosition = isCutout
              ? getBackgroundPosition(data.image.position || 'bottom')
              : getBackgroundPosition(data.image.position);
            const baseImgStyles: React.CSSProperties = {
              objectFit: rawSize === 'contain' ? 'contain' : 'cover',
              objectPosition,
            };
            const altText = data.image.alt || 'Split hero image';

            return (
              <div
                className={`split-hero-image ${imageOrder} relative overflow-hidden ${imageMinHeight}`}
              >
                <div className="absolute inset-0 h-full w-full overflow-hidden">
                  <img
                    src={imageUrl}
                    alt={altText}
                    data-seo-image-role="content"
                    className="absolute inset-0 h-full w-full"
                    style={hasEffects ? { ...baseImgStyles, ...cropStyles } : baseImgStyles}
                  />
                </div>
              </div>
            );
          })()}
      </div>
    </section>
  );
};
